import React, {useEffect, useMemo, useState} from "react";
import {Prompt} from "react-router-dom";
import fileDownload from "js-file-download";
import {Button, CircularProgress, Divider, IconButton} from "@mui/material";
import {
    Delete as DeleteIcon,
    Download as DownloadIcon,
    Edit as EditIcon,
    NavigateBefore as PrevIcon,
    NavigateNext as NextIcon,
    Visibility as PreviewIcon,
    YouTube as WatchIcon,
} from "@mui/icons-material";
import {usePaging} from "@atttomyx/shared-hooks";
import {
    Cards,
    ConfirmDeleteDialog,
    FloatingAddButton,
    TabBar,
    TabPanel,
    VerticalDivider,
} from "@atttomyx/react-components";
import {CourseForm} from "../forms";
import {DocumentCard, FormCard, TemplateCard, UpsellCard, VideoCard} from "../cards";
import {
    AddDocumentDialog,
    AddUpsellDialog,
    ChooseQuestionnaireDialog,
    ChooseTemplateDialog,
    EditFileDialog,
    EditUpsellDialog,
    EditVideoDialog,
    PreviewHtmlDialog,
    SubmissionDialog,
    YouTubeDialog
} from "../dialogs";
import {courseService, documentService} from "../../services";
import {arrays, forms, objects, sorting, strings} from "@atttomyx/shared-utils";
import {sortingUtils} from "../../utils";
import {verbiage} from "@atttomyx/shared-constants";
import "./editCoursePage.css";

const toData = (course) => {
    const documentIds = objects.defaultIfNullOrUndefined(course.documentIds, []);
    const videoIds = objects.defaultIfNullOrUndefined(course.videoIds, []);
    const questionnaireIds = objects.defaultIfNullOrUndefined(course.questionnaireIds, []);
    const prerequisiteIds = objects.defaultIfNullOrUndefined(course.prerequisiteIds, []);
    const templateIds = objects.defaultIfNullOrUndefined(course.templateIds, []);
    const upsellIds = objects.defaultIfNullOrUndefined(course.upsellIds, []);

    return {
        name: course.name,
        description: course.description,
        imageUrl: course.imageUrl,
        gotoUrl: course.gotoUrl,
        gotoLabel: course.gotoLabel,
        music: course.music,
        ranks: course.ranks,
        status: course.status,
        order: course.order,
        documentIds: arrays.copy(documentIds),
        videoIds: arrays.copy(videoIds),
        questionnaireIds: arrays.copy(questionnaireIds),
        prerequisiteIds: arrays.copy(prerequisiteIds),
        templateIds: arrays.copy(templateIds),
        upsellIds: arrays.copy(upsellIds),
        valid: strings.isNotBlank(course.name) && strings.isNotBlank(course.status) && sortingUtils.isSortable(course.order),
    }
};

const EditCoursePage = (props) => {
    const {
        snackbar, dimensions, match, user, courses, documents, videos, upsells, questionnaires, questions, templates, filters,
        onSaveCourse, onSaveDocument, onSaveVideo, onSaveUpsell, onPrev, onNext
    } = props;
    const [data, setData] = useState(toData({}));
    const [course, setCourse] = useState({});
    const [saving, setSaving] = useState(false);
    const [downloading, setDownloading] = useState(null);
    const [watching, setWatching] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [preview, setPreview] = useState(null);
    const [showAdd, setShowAdd] = useState(false);
    const [edit, setEdit] = useState(null);
    const [prevId, setPrevId] = useState(null);
    const [nextId, setNextId] = useState(null);
    const [tab, setTab] = useState(0);
    const contentPaging = usePaging();
    const homeworkPaging = usePaging();
    const templatesPaging = usePaging();
    const upsellsPaging = usePaging();

    const documentsToUse = useMemo(() => {
        const array = [];

        documents
        .filter(document => arrays.contains(data.documentIds, document.id))
        .forEach(document => arrays.addTo(array, document));

        return array;
    }, [data, documents]);

    const videosToUse = useMemo(() => {
        const array = [];

        videos
        .filter(video => arrays.contains(data.videoIds, video.id))
        .forEach(video => arrays.addTo(array, video));

        return array;
    }, [data, videos]);

    const questionnairesToUse = useMemo(() => {
        const array = [];

        questionnaires
        .filter(questionnaire => arrays.contains(data.questionnaireIds, questionnaire.id)
            || arrays.contains(data.prerequisiteIds, questionnaire.id))
        .forEach(questionnaire => arrays.addTo(array, questionnaire));

        return array.sort(sorting.sortByOrder);
    }, [data, questionnaires]);

    const templatesToUse = useMemo(() => {
        const array = [];

        templates
        .filter(template => arrays.contains(data.templateIds, template.id))
        .forEach(template => arrays.addTo(array, template));

        return array.sort(sorting.sortByOrder);
    }, [data, templates]);

    const upsellsToUse = useMemo(() => {
        const array = [];

        upsells
        .filter(upsell => arrays.contains(data.upsellIds, upsell.id))
        .forEach(upsell => arrays.addTo(array, upsell));

        return array.sort(sorting.sortByOrder);
    }, [data, upsells]);

    const questionsToUse = useMemo(() => {
        return preview && tab === 1
            ? questions.filter(question => question.questionnaireId === preview.id)
            : [];
    }, [tab, preview, questions]);

    const items = useMemo(() => {
        const array = [];

        arrays.addAll(array, videosToUse);
        arrays.addAll(array, documentsToUse);

        return array.sort(sorting.sortByOrder);
    }, [documentsToUse, videosToUse]);

    const nextOrderContent = useMemo(() => {
        let next = items.length;

        items.forEach(item => {
            if (item.order >= next) {
                next = item.order + 1;
            }
        });

        return next;
    }, [items]);

    const nextOrderUpsell = useMemo(() => {
        let next = upsellsToUse.length;

        upsellsToUse.forEach(upsell => {
            if (upsell.order >= next) {
                next = upsell.order + 1;
            }
        });

        return next;
    }, [upsellsToUse]);

    const tabLabels = useMemo(() => {
        const labels = [];

        arrays.addTo(labels, `Content (${items.length})`);
        arrays.addTo(labels, `Homework (${questionnairesToUse.length})`);
        arrays.addTo(labels, `Generated (${templatesToUse.length})`);
        arrays.addTo(labels, `Upsells (${upsellsToUse.length})`);

        return labels;
    }, [items, questionnairesToUse, templatesToUse, upsellsToUse]);

    const loadCourse = (courseId) => {
        const filtered = filters.filter(courses);
        const found = arrays.findEntity(filtered, courseId) || {};
        const ids = arrays.getPrevNextIds(filtered, found);

        setCourse(found);
        setPrevId(ids.prevId);
        setNextId(ids.nextId);
    };

    const redirectToPrev = () => {
        onPrev(prevId);
        loadCourse(prevId);
    }

    const redirectToNext = () => {
        onNext(nextId);
        loadCourse(nextId);
    }

    useEffect(() => {
        const courseId = match.params.id;

        loadCourse(courseId);
    }, []);

    useEffect(() => {
        setData(toData(course));
    }, [course]);

    const resetForm = () => {
        setData(toData(course));
    };

    const submitForm = () => {
        setSaving(true);

        const modified = {
            name: data.name,
            description: data.description,
            imageUrl: data.imageUrl,
            gotoUrl: data.gotoUrl,
            gotoLabel: data.gotoLabel,
            music: data.music,
            ranks: data.ranks,
            status: data.status,
            order: data.order,
            documentIds: data.documentIds,
            videoIds: data.videoIds,
            questionnaireIds: data.questionnaireIds,
            prerequisiteIds: data.prerequisiteIds,
            templateIds: data.templateIds,
            upsellIds: data.upsellIds,
        };

        const success = (course) => {
            setCourse(course);
            setSaving(false);

            snackbar.setSuccess("Module saved");
            onSaveCourse(course);
        };

        const failure = (err) => {
            setSaving(false);
            snackbar.setError(err);
        };

        courseService.updateCourse(course.id, modified, success, failure);
    };

    const downloadFile = (document) => {
        setDownloading(document);

        const success = (bytes) => {
            fileDownload(bytes, document.fileName);
            setDownloading(null);
        };

        const failure = (err) => {
            snackbar.setError(err);
            setDownloading(null);
        };

        documentService.downloadFile(document.id, success, failure);
    };

    const addDocumentToCourse = (documentId) => {
        if (!arrays.contains(data.documentIds, documentId)) {
            const modified = objects.deepCopy(data);

            arrays.addTo(modified.documentIds, documentId);

            setData(modified);
        }
    };

    const addVideoToCourse = (videoId) => {
        if (!arrays.contains(data.videoIds, videoId)) {
            const modified = objects.deepCopy(data);

            arrays.addTo(modified.videoIds, videoId);

            setData(modified);
        }
    };

    const addQuestionnaireToCourse = (questionnaireId, prerequisite) => {
        const field = prerequisite ? "prerequisiteIds" : "questionnaireIds";

        if (!arrays.contains(data[field], questionnaireId)) {
            const modified = objects.deepCopy(data);

            arrays.addTo(modified[field], questionnaireId);

            setData(modified);
        }
    };

    const addTemplateToCourse = (templateId) => {
        if (!arrays.contains(data.templateIds, templateId)) {
            const modified = objects.deepCopy(data);

            arrays.addTo(modified.templateIds, templateId);

            setData(modified);
        }
    };

    const addUpsellToCourse = (upsellId) => {
        if (!arrays.contains(data.upsellIds, upsellId)) {
            const modified = objects.deepCopy(data);

            arrays.addTo(modified.upsellIds, upsellId);

            setData(modified);
        }
    };

    const removeDocumentFromCourse = (documentId) => {
        if (arrays.contains(data.documentIds, documentId)) {
            const modified = objects.deepCopy(data);

            arrays.removeFrom(modified.documentIds, documentId);

            setData(modified);
        }
    };

    const removeVideoFromCourse = (videoId) => {
        if (arrays.contains(data.videoIds, videoId)) {
            const modified = objects.deepCopy(data);

            arrays.removeFrom(modified.videoIds, videoId);

            setData(modified);
        }
    };

    const removeQuestionnaireFromCourse = (questionnaireId) => {
        if (arrays.contains(data.questionnaireIds, questionnaireId)) {
            const modified = objects.deepCopy(data);

            arrays.removeFrom(modified.questionnaireIds, questionnaireId);

            setData(modified);
        }

        if (arrays.contains(data.prerequisiteIds, questionnaireId)) {
            const modified = objects.deepCopy(data);

            arrays.removeFrom(modified.prerequisiteIds, questionnaireId);

            setData(modified);
        }
    };

    const removeTemplateFromCourse = (templateId) => {
        if (arrays.contains(data.templateIds, templateId)) {
            const modified = objects.deepCopy(data);

            arrays.removeFrom(modified.templateIds, templateId);

            setData(modified);
        }
    };

    const removeUpsellFromCourse = (upsellId) => {
        if (arrays.contains(data.upsellIds, upsellId)) {
            const modified = objects.deepCopy(data);

            arrays.removeFrom(modified.upsellIds, upsellId);

            setData(modified);
        }
    };

    const valid = data.valid;
    const modified = forms.differ(toData(course), data);

    return saving ?
        <div className="edit-course-page">
            <CircularProgress size="80px"/>
        </div> :
        <div className="edit-course-page">
            <CourseForm
                snackbar={snackbar}
                courseId={course.id}
                data={data}
                onChange={setData}
            />
            <div className="actions">
                <IconButton color="secondary" title="Previous course"
                            disabled={!prevId}
                            onClick={redirectToPrev}>
                    <PrevIcon/>
                </IconButton>
                <IconButton color="secondary" title="Next course"
                            disabled={!nextId}
                            onClick={redirectToNext}>
                    <NextIcon/>
                </IconButton>
                <VerticalDivider/>
                <Button color="secondary" variant="text"
                        disabled={!modified}
                        onClick={resetForm}>
                    Undo
                </Button>
                <Button color="primary" size="large"
                        disabled={!valid || !modified}
                        onClick={submitForm}>
                    Save
                </Button>
            </div>
            <Divider/>
            <TabBar
                tabs={tabLabels}
                value={tab}
                onChange={setTab}
            />
            <TabPanel value={tab} index={0}>
                <Cards
                    items={items}
                    paging={contentPaging}
                    dimensions={dimensions}
                    renderer={(item) =>
                        item.title ?
                            <VideoCard key={item.id} video={item}>
                                <IconButton
                                    color="secondary" title="Delete" className="delete"
                                    onClick={() => setDeleting(item)}
                                >
                                    <DeleteIcon/>
                                </IconButton>
                                <IconButton
                                    color="primary" title="Watch"
                                    onClick={() => setWatching(item)}
                                >
                                    <WatchIcon/>
                                </IconButton>
                                <IconButton
                                    color="primary" title="Edit"
                                    onClick={() => setEdit(item)}
                                >
                                    <EditIcon/>
                                </IconButton>
                            </VideoCard> :
                            <DocumentCard key={item.id} document={item}>
                                <IconButton
                                    color="secondary" title="Delete" className="delete"
                                    onClick={() => setDeleting(item)}
                                >
                                    <DeleteIcon/>
                                </IconButton>
                                {downloading && downloading.id === item.id ?
                                    <CircularProgress size="24px" className="downloading"/> :
                                    <IconButton
                                        color="primary" title="Download"
                                        onClick={() => downloadFile(item)}
                                    >
                                        <DownloadIcon/>
                                    </IconButton>}
                                <IconButton
                                    color="primary" title="Edit"
                                    onClick={() => setEdit(item)}
                                >
                                    <EditIcon/>
                                </IconButton>
                            </DocumentCard>}
                />
            </TabPanel>
            <TabPanel value={tab} index={1}>
                <Cards
                    items={questionnairesToUse}
                    paging={homeworkPaging}
                    dimensions={dimensions}
                    renderer={(item) =>
                        <FormCard
                            key={item.id}
                            questionnaire={item}
                            prerequisite={arrays.contains(data.prerequisiteIds, item.id)}
                            filters={filters}
                        >
                            <IconButton
                                color="secondary" title="Delete" className="delete"
                                onClick={() => setDeleting(item)}
                            >
                                <DeleteIcon/>
                            </IconButton>
                            <IconButton
                                color="primary" title="Preview"
                                onClick={() => setPreview(item)}
                            >
                                <PreviewIcon/>
                            </IconButton>
                        </FormCard>}
                />
            </TabPanel>
            <TabPanel value={tab} index={2}>
                <Cards
                    items={templatesToUse}
                    paging={templatesPaging}
                    dimensions={dimensions}
                    renderer={(item) =>
                        <TemplateCard
                            key={item.id}
                            template={item}
                            filters={filters}
                        >
                            <IconButton
                                color="secondary" title="Delete" className="delete"
                                onClick={() => setDeleting(item)}
                            >
                                <DeleteIcon/>
                            </IconButton>
                            <IconButton
                                color="primary" title="Preview"
                                onClick={() => setPreview(item)}
                            >
                                <PreviewIcon/>
                            </IconButton>
                        </TemplateCard>}
                />
            </TabPanel>
            <TabPanel value={tab} index={3}>
                <Cards
                    items={upsellsToUse}
                    paging={upsellsPaging}
                    dimensions={dimensions}
                    renderer={(item) =>
                        <UpsellCard
                            key={item.id}
                            upsell={item}
                            filters={filters}
                        >
                            <IconButton
                                color="secondary" title="Delete" className="delete"
                                onClick={() => setDeleting(item)}
                            >
                                <DeleteIcon/>
                            </IconButton>
                            <IconButton
                                color="primary" title="Edit"
                                onClick={() => setEdit(item)}
                            >
                                <EditIcon/>
                            </IconButton>
                        </UpsellCard>}
                />
            </TabPanel>
            <FloatingAddButton
                title={`Add ${tab === 0 ? "content" : tab === 1 ? "homework" : "asset"}`}
                position={dimensions.portrait ? "higher" : undefined}
                center={false}
                disabled={courses.length < 2}
                onClick={() => {
                    setShowAdd(true);
                }}
            />
            {showAdd ? tab === 0 ?
                <AddDocumentDialog
                    snackbar={snackbar}
                    courseId={course.id}
                    courses={courses}
                    documents={documents}
                    order={nextOrderContent}
                    onCancel={() => setShowAdd(false)}
                    onSaveDocument={(document) => {
                        setShowAdd(false);
                        onSaveDocument(document);
                        addDocumentToCourse(document.id);
                    }}
                    onSaveVideo={(video) => {
                        setShowAdd(false);
                        onSaveVideo(video);
                        addVideoToCourse(video.id);
                    }}
                    onExistingDocument={(document) => {
                        setShowAdd(false);
                        addDocumentToCourse(document.id);
                    }}
                /> : tab === 1 ?
                <ChooseQuestionnaireDialog
                    questionnaires={questionnaires}
                    onCancel={() => setShowAdd(false)}
                    onChoose={(questionnaireId, prerequisite) => {
                        setShowAdd(false);
                        addQuestionnaireToCourse(questionnaireId, prerequisite);
                    }}
                /> : tab === 2 ?
                <ChooseTemplateDialog
                    templates={templates}
                    onCancel={() => setShowAdd(false)}
                    onChoose={(templateId) => {
                        setShowAdd(false);
                        addTemplateToCourse(templateId);
                    }}
                /> : tab === 3 ?
                <AddUpsellDialog
                    snackbar={snackbar}
                    order={nextOrderUpsell}
                    onCancel={() => setShowAdd(false)}
                    onSave={(upsell) => {
                        setShowAdd(false);
                        onSaveUpsell(upsell);
                        addUpsellToCourse(upsell.id);
                    }}
                /> : null : null}
            {edit ? tab === 0 && edit.title ?
                <EditVideoDialog
                    snackbar={snackbar}
                    video={edit}
                    onCancel={() => setEdit(null)}
                    onSave={(saved) => {
                        setEdit(null);
                        onSaveVideo(saved);
                    }}
                /> : tab === 0 ?
                <EditFileDialog
                    snackbar={snackbar}
                    file={edit}
                    onCancel={() => setEdit(null)}
                    onSave={(saved) => {
                        setEdit(null);
                        onSaveDocument(saved);
                    }}
                /> : tab === 3 ?
                <EditUpsellDialog
                    snackbar={snackbar}
                    upsell={edit}
                    onCancel={() => setEdit(null)}
                    onSave={(saved) => {
                        setEdit(null);
                        onSaveUpsell(saved);
                    }}
                /> : null : null}
            {watching ?
                <YouTubeDialog
                    title={watching.title}
                    videoId={watching.youTubeId}
                    onClose={() => setWatching(null)}
                /> : null}
            {deleting ?
                <ConfirmDeleteDialog
                    snackbar={snackbar}
                    title={tab === 0 ? deleting.title ? deleting.title : deleting.fileName
                        : tab === 1 ? deleting.title
                        : tab === 2 ? deleting.name
                        : deleting.id}
                    onCancel={() => setDeleting(null)}
                    onDelete={(itemId) => setDeleting(null)}
                    deleter={(success, failure) => {
                        if (tab === 0) {
                            if (deleting.title) {
                                removeVideoFromCourse(deleting.id);

                            } else {
                                removeDocumentFromCourse(deleting.id);
                            }

                        } else if (tab === 1) {
                            removeQuestionnaireFromCourse(deleting.id);

                        } else if (tab === 2) {
                            removeTemplateFromCourse(deleting.id);

                        } else if (tab === 3) {
                            removeUpsellFromCourse(deleting.id);
                        }

                        success(deleting.id);
                    }}
                /> : null}
            {preview ? tab === 1 ?
                <SubmissionDialog
                    snackbar={snackbar}
                    theme={user.settings.theme}
                    questionnaire={preview}
                    questions={questionsToUse}
                    preview={true}
                    onCancel={() => setPreview(null)}
                /> : tab === 2 ?
                <PreviewHtmlDialog
                    html={preview.html}
                    onCancel={() => setPreview(null)}
                /> : null : null}
            <Prompt when={modified} message={verbiage.UNSAVED_CHANGES}/>
        </div>
}

export default EditCoursePage;
